import type { UserProfile } from '../types/auth.types';
import { authErrorMessage } from '../utils/authError';
import { APP_CONFIG } from '../../shared/config/app.config';
import {
  clearSession,
  getSession,
  initSession,
  onSessionChange,
  refreshSession,
  saveSession,
  sessionFromResponse,
  userFromAccessToken,
  type GoTrueSessionResponse,
} from './authSession';

const BACKEND_URL = APP_CONFIG.API.BACKEND_URL;
const AUTH_URL = APP_CONFIG.AUTH.URL;

async function postToAuth(path: string, body: unknown, headers: Record<string, string> = {}) {
  try {
    return await fetch(`${AUTH_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('No se puede conectar con el servidor. Revisa tu conexión.');
  }
}

export const AuthService = {
  initSession,
  onSessionChange,
  userFromAccessToken,

  /**
   * Envía un código de un solo uso al email. Si el email no tiene cuenta, GoTrue
   * la crea: registro e inicio de sesión son el mismo flujo.
   */
  async sendOtp(email: string): Promise<void> {
    const response = await postToAuth('/otp', { email, create_user: true });
    if (!response.ok) {
      throw new Error(await authErrorMessage(response, 'No se ha podido enviar el código.'));
    }
  },

  /** Verifica el código y guarda la sesión (notifica a onSessionChange). */
  async verifyOtp(email: string, token: string): Promise<void> {
    const response = await postToAuth('/verify', { type: 'email', email, token });
    if (!response.ok) {
      throw new Error(await authErrorMessage(response, 'No se ha podido verificar el código.'));
    }
    saveSession(sessionFromResponse((await response.json()) as GoTrueSessionResponse));
  },

  async deleteMyAccount(token: string): Promise<void> {
    const res = await fetch(`${BACKEND_URL}/users/me`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok && res.status !== 204) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? `HTTP ${res.status}`);
    }
  },

  async signOut(): Promise<void> {
    const session = getSession();
    clearSession();
    if (session) {
      // Best effort: si falla, el refresh token caduca solo
      await postToAuth('/logout', {}, { Authorization: `Bearer ${session.accessToken}` }).catch(
        () => undefined,
      );
    }
  },

  // Sync getter — el access token vigente (se renueva en segundo plano)
  getToken(): string | null {
    return getSession()?.accessToken ?? null;
  },

  async syncProfile(token: string, fullName?: string): Promise<UserProfile | null> {
    try {
      const response = await fetch(`${BACKEND_URL}/users/me/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fullName }),
      });
      if (!response.ok) return null;
      return response.json() as Promise<UserProfile>;
    } catch {
      return null;
    }
  },

  async updateMyName(token: string, fullName: string): Promise<UserProfile> {
    const response = await fetch(`${BACKEND_URL}/users/me/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ fullName }),
    });
    if (!response.ok) throw new Error('No se pudo guardar tu nombre. Inténtalo de nuevo.');
    return response.json() as Promise<UserProfile>;
  },

  /**
   * Perfil local del usuario. Un 401 del backend NO cierra la sesión por sí
   * solo: puede ser un access token caducado cuya renovación se quedó dormida
   * (pestaña en segundo plano, móvil suspendido) o el backend sin poder
   * validar tokens en ese momento (reinicio del servidor). Se renueva contra
   * GoTrue y se reintenta; la sesión solo muere si GoTrue rechaza el refresh
   * token (cuenta borrada, sesión revocada, refresh ya usado).
   */
  async getMyProfile(token: string): Promise<UserProfile | null> {
    try {
      let response = await fetch(`${BACKEND_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 401) {
        const renewed = await refreshSession({ rejectedToken: token });
        // Sin sesión: refreshSession ya la ha cerrado. Si GoTrue falló de forma
        // temporal devuelve la misma y no hay con qué reintentar: se entra sin
        // perfil y el timer de renovación lo volverá a intentar.
        if (!renewed || renewed.accessToken === token) return null;
        response = await fetch(`${BACKEND_URL}/users/me`, {
          headers: { Authorization: `Bearer ${renewed.accessToken}` },
        });
        // Token recién emitido y aun así 401: es el backend el que no puede
        // validar ahora (p. ej. sin acceso a las claves de GoTrue). No es
        // motivo para echar al usuario.
        if (response.status === 401) return null;
      }
      if (!response.ok) return null;
      // Sin perfil local todavía (primer acceso) el backend responde 200 con el
      // cuerpo vacío: `response.json()` reventaría y, al no estar esperado
      // dentro del try, el error se escapaba y el login se quedaba colgado en
      // "Verificando...". Cuerpo vacío = sin perfil → se creará con sync.
      const text = await response.text();
      return text ? (JSON.parse(text) as UserProfile) : null;
    } catch {
      return null;
    }
  },
};
